import {TODO_COLUMN_STATUSES} from '../../../core/entities/entryTodos/constants';
import type {EntryTodoType} from '../../../core/repositories/entryTodosRepository';

/**
 * ステータスがTODOカラム（todo/doing）に属するかを判定する関数
 *
 * UIでは2カラム（TODO/DONE）表示だが、内部的にはdoingステータスも
 * TODOカラムに含めて扱う。ドラッグ&ドロップ時のカラム判定に使用。
 *
 * @param status - 判定対象のステータス
 * @returns TODOカラムに属する場合はtrue、DONEカラムの場合はfalse
 */
export const isInTodoColumn = (status: string): boolean => {
  return TODO_COLUMN_STATUSES.includes(
    status as (typeof TODO_COLUMN_STATUSES)[number],
  );
};

/**
 * 検索条件に基づいてTODOをフィルタリングする統合関数
 *
 * フィルタリングの動作:
 * - company: 企業名が完全一致するTODOのみを返す
 * - todo: ToDo名が完全一致するTODOのみを返す
 * - partial: 企業名またはToDo名に部分一致するTODOを返す（大文字小文字を区別しない）
 *
 * @param todos - フィルタリング対象のTODO配列
 * @param filterType - フィルタータイプ（'company' | 'todo' | 'partial'）
 * @param searchValue - 検索値
 * @returns フィルタリングされたTODO配列
 */
export const filterTodos = (
  todos: EntryTodoType[],
  filterType: 'company' | 'todo' | 'partial',
  searchValue: string,
): EntryTodoType[] => {
  switch (filterType) {
    case 'company':
      return todos.filter((todo) => todo.company?.companyName === searchValue);
    case 'todo':
      return todos.filter((todo) => todo.title === searchValue);
    case 'partial': {
      const lowerSearchValue = searchValue.toLowerCase();
      return todos.filter((todo) => {
        const companyMatch = todo.company?.companyName
          ?.toLowerCase()
          .includes(lowerSearchValue);
        const titleMatch = todo.title.toLowerCase().includes(lowerSearchValue);
        return companyMatch || titleMatch;
      });
    }
    default:
      return todos;
  }
};

/**
 * 指定したエントリー管理IDに該当するTODOが存在するかを判定する関数
 * @param todos - 判定対象のTODO配列
 * @param entryManagementId - 判定対象のエントリー管理ID
 * @returns 該当するTODOが存在する場合はtrue
 */
export const checkHasTodosForEntryManagement = (
  todos: EntryTodoType[],
  entryManagementId: number | null,
): boolean => {
  if (!entryManagementId) return false;
  return todos.some((todo) => todo.userEntryManagementID === entryManagementId);
};
